import {
  EmployerFooter,
  EmployerHeader,
  type AnalyticsEvent,
} from '@seek/adv-header-footer';
import { ManagedContentBanner } from '@seek/cms-ui';
import { useTranslations } from '@vocab/react';
import { ContentBlock, Page } from 'braid-design-system';
import type { FC, ReactNode } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { useAnalytics } from 'src/modules/analytics';
import { useConfig } from 'src/modules/appConfig';
import { useAccountInfo } from 'src/modules/dataManager';
import { useHeaderFooterBasicState } from 'src/modules/headerFooterBasicContext';
import { useSelector } from 'src/store/hooks';
import { isEnvStaging } from 'src/utils/isEnvironment';
import PendingActivationBanner from 'src/views/shared/PendingActivationBanner';
import UnableToLoadUserError from 'src/views/shared/UnableToLoadUserError';

import translations from './.vocab';

interface Props {
  children: ReactNode;
}

export const MainLayout: FC<Props> = ({ children }: Props) => {
  const { t } = useTranslations(translations);

  const { headerFooterContext } = useAccountInfo();
  const { locale, site, environment } = useConfig();
  const { trackLink } = useAnalytics();
  // @TODO: CLEANUP | Remove once user api endpoint no longer in use
  const userApiError = useSelector((state) => state.userApiError);

  const { headerBasicState, footerBasicState } = useHeaderFooterBasicState();

  const isPendingActivation =
    headerFooterContext?.advertiser?.isActivationPending ?? false;

  const showUnableToLoadUserError = userApiError;

  const showPendingActivationBanner = isPendingActivation;

  const showBannerContentBlock =
    showUnableToLoadUserError || showPendingActivationBanner;

  return (
    <HelmetProvider>
      <Page
        footer={
          <EmployerFooter
            site={site}
            locale={locale}
            isStaging={isEnvStaging(environment)}
            onTrackedItemInteraction={(analyticsEvent: AnalyticsEvent) => {
              trackLink(analyticsEvent.eventName, { ...analyticsEvent });
            }}
            advertiser={headerFooterContext?.advertiser}
            identityContext={headerFooterContext?.identityContext}
            basic={footerBasicState}
          />
        }
        footerPosition="belowFold"
      >
        <Helmet title={t('Page Title')} />
        <EmployerHeader
          id="employer-header"
          site={site}
          locale={locale}
          isStaging={isEnvStaging(environment)}
          onTrackedItemInteraction={(analyticsEvent: AnalyticsEvent) => {
            trackLink(analyticsEvent.eventName, { ...analyticsEvent });
          }}
          advertiser={headerFooterContext?.advertiser}
          identityContext={headerFooterContext?.identityContext}
          user={headerFooterContext?.user}
          basic={headerBasicState}
        />
        <main>
          {!showPendingActivationBanner && (
            <ManagedContentBanner
              platform="employer"
              context="hirer_invoices"
              environment={environment === 'dev' ? 'staging' : environment}
              locale={locale}
            />
          )}
          {showBannerContentBlock ? (
            <ContentBlock>
              {showUnableToLoadUserError && <UnableToLoadUserError />}
              {showPendingActivationBanner && <PendingActivationBanner />}
            </ContentBlock>
          ) : null}
          {children}
        </main>
      </Page>
    </HelmetProvider>
  );
};
